import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { getSavedSearches, updateSavedSearch, deleteSavedSearch } from '@/lib/api';
import { toast } from 'sonner';
import { Bell, BellOff, Trash2, Search, ArrowRight } from 'lucide-react';

const describe = (f = {}) => {
  const parts = [];
  if (f.listing_type) parts.push(f.listing_type === 'rent' ? 'For rent' : 'For sale');
  if (f.property_type) parts.push(f.property_type);
  if (f.bedrooms) parts.push(`${f.bedrooms}+ BHK`);
  if (f.city) parts.push(f.city);
  if (f.locality) parts.push(f.locality);
  if (f.max_price) parts.push(`under ₹${Number(f.max_price).toLocaleString('en-IN')}`);
  return parts.join(' · ') || 'All properties';
};

const toQuery = (f = {}) => {
  const p = new URLSearchParams();
  Object.entries(f).forEach(([k, v]) => { if (v) p.append(k, v); });
  return p.toString();
};

export const SavedSearches = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => { setLoading(true); getSavedSearches().then(setRows).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const toggle = async (r) => {
    try { await updateSavedSearch(r.search_id, { alerts_enabled: !r.alerts_enabled }); setRows((p) => p.map((x) => x.search_id === r.search_id ? { ...x, alerts_enabled: !x.alerts_enabled } : x)); } catch { toast.error('Failed'); }
  };
  const remove = async (r) => {
    if (!window.confirm(`Delete saved search "${r.name}"?`)) return;
    try { await deleteSavedSearch(r.search_id); setRows((p) => p.filter((x) => x.search_id !== r.search_id)); toast.success('Deleted'); } catch { toast.error('Failed'); }
  };

  if (loading) return <p className="text-slate-500 py-8">Loading…</p>;
  if (rows.length === 0) return (
    <div className="text-center py-16 bg-white border border-slate-200 rounded-2xl" data-testid="saved-searches-empty">
      <Search className="h-8 w-8 mx-auto text-slate-300 mb-3" />
      <p className="text-slate-600 font-medium">No saved searches yet.</p>
      <p className="text-sm text-slate-500 mt-1">Run a search on the Properties page and tap "Save this search" to get alerts when new matches are posted.</p>
      <Link to="/properties"><Button className="mt-4 rounded-full bg-[#0F172A] text-white">Browse properties</Button></Link>
    </div>
  );

  return (
    <div className="space-y-3" data-testid="saved-searches-list">
      {rows.map((r) => (
        <div key={r.search_id} className="bg-white border border-slate-200 rounded-xl p-4 flex flex-wrap items-center gap-4" data-testid={`saved-search-${r.search_id}`}>
          <div className="min-w-0 flex-1">
            <p className="font-bold text-[#0F172A]">{r.name}</p>
            <p className="text-sm text-slate-500">{describe(r.filters)}</p>
          </div>
          <Link to={`/properties?${toQuery(r.filters)}`}><Button variant="outline" size="sm" className="rounded-full">View results <ArrowRight className="h-3.5 w-3.5 ml-1.5" /></Button></Link>
          <button onClick={() => toggle(r)} title={r.alerts_enabled ? 'Alerts on' : 'Alerts off'} className={`inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-full border ${r.alerts_enabled ? 'text-emerald-700 border-emerald-200 bg-emerald-50' : 'text-slate-500 border-slate-200'}`} data-testid={`saved-search-alert-${r.search_id}`}>
            {r.alerts_enabled ? <Bell className="h-3.5 w-3.5" /> : <BellOff className="h-3.5 w-3.5" />} {r.alerts_enabled ? 'Email alerts on' : 'Alerts off'}
          </button>
          <button onClick={() => remove(r)} className="p-2 hover:bg-red-50 rounded-lg" data-testid={`saved-search-delete-${r.search_id}`}><Trash2 className="h-4 w-4 text-red-500" /></button>
        </div>
      ))}
    </div>
  );
};

export default SavedSearches;
